import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Search, GripVertical, RotateCcw, Eye, EyeOff, X, Save } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  order: number;
  group?: 'default' | 'optional';
}

interface ConfigureColumnsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  onReset: () => void;
}

interface SortableColumnItemProps {
  column: ColumnConfig;
  onToggleVisibility: (id: string) => void;
  searchQuery: string;
}

function SortableColumnItem({ column, onToggleVisibility, searchQuery }: SortableColumnItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Highlight matching text
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, i) => 
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="bg-yellow-200 dark:bg-yellow-900 font-semibold">
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 p-3 rounded-md border bg-card transition-colors",
        isDragging && "opacity-50 shadow-lg z-50",
        !column.visible && "opacity-60"
      )}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </div>

      {/* Column Label */}
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium">
          {highlightText(column.label, searchQuery)}
        </span>
      </div>

      {/* Visibility Icon */}
      <div className="flex items-center gap-2">
        {column.visible ? (
          <Eye className="h-4 w-4 text-muted-foreground" />
        ) : (
          <EyeOff className="h-4 w-4 text-muted-foreground" />
        )}
        
        {/* Toggle Switch */}
        <Switch
          checked={column.visible}
          onCheckedChange={() => onToggleVisibility(column.id)}
          aria-label={`Toggle ${column.label} visibility`}
        />
      </div>
    </div>
  );
}

export function ConfigureColumnsDialog({
  open,
  onOpenChange,
  columns,
  onColumnsChange,
  onReset,
}: ConfigureColumnsDialogProps): React.ReactElement {
  // Local state for draft changes
  const [draftColumns, setDraftColumns] = useState<ColumnConfig[]>(columns);
  const [searchQuery, setSearchQuery] = useState('');

  // Sync draft columns when dialog opens or columns prop changes
  useEffect(() => {
    if (open) {
      setDraftColumns(columns);
      setSearchQuery('');
    }
  }, [open, columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Filter columns based on search
  const filteredColumns = useMemo(() => {
    if (!searchQuery) return draftColumns;
    return draftColumns.filter(col =>
      col.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [draftColumns, searchQuery]);

  // Stats
  const visibleCount = draftColumns.filter(col => col.visible).length;
  const totalCount = draftColumns.length;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setDraftColumns((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        const reordered = arrayMove(items, oldIndex, newIndex);
        return reordered.map((col, index) => ({ ...col, order: index }));
      });
    }
  };

  const handleToggleVisibility = (id: string) => {
    setDraftColumns((prev) =>
      prev.map((col) =>
        col.id === id ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const handleReset = () => {
    onReset();
    setDraftColumns(columns);
    setSearchQuery('');
  };

  const handleSave = () => {
    onColumnsChange(draftColumns);
    onOpenChange(false);
  };

  const handleClose = () => {
    // Discard changes
    setDraftColumns(columns);
    setSearchQuery('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[85vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-2xl font-semibold">
            Configure Columns
          </DialogTitle>
          <DialogDescription className="mt-2">
            Customize which columns are visible and their order.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 space-y-4 flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search columns..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
              className="pl-9"
              autoComplete="off"
            />
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Showing {visibleCount} of {totalCount} columns
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="h-8"
            >
              <RotateCcw className="mr-2 h-3.5 w-3.5" />
              Reset to Defaults
            </Button>
          </div>

          {/* Columns List */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredColumns.map(col => col.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2 pb-4">
                  {filteredColumns.map((column) => (
                    <SortableColumnItem
                      key={column.id}
                      column={column}
                      onToggleVisibility={handleToggleVisibility}
                      searchQuery={searchQuery}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* Footer with Save and Close buttons */}
        <DialogFooter className="px-6 py-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
          >
            <Save className="mr-2 h-4 w-4" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
